import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import {NgForm} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {NzMessageService} from "ng-zorro-antd/message";
import {lastValueFrom} from "rxjs";
import {
    InternalSettingDto,
    PerFormaAPIService,
    SchemaDto,
    SchemaKeywordDto,
    UploadKeywordDto
} from "src/app/services/pfapi.service";
import {KeywordTypes} from "src/app/models/performa-enums";

type SchemaKeywordDtoExt = SchemaKeywordDto & {
    DateValue?: Date;
    TextValue?: string;
};

@Component({
    selector: "app-upload",
    templateUrl: "./upload.component.html",
    styleUrl: "./upload.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UploadComponent implements OnInit {
    constructor(
        private readonly api: PerFormaAPIService,
        private route: ActivatedRoute,
        private message: NzMessageService
    ) {}

    loading = true;
    schema: SchemaDto = {Types: [], Keywords: []};
    commonFields: SchemaKeywordDtoExt[] = [];
    documentType: number;
    hasChosenType: boolean;
    isFromPTP: boolean;
    hasChosenFile: boolean;
    submitted = false;
    showTypeError = false;
    poId: number;
    poMetaData: any;

    @ViewChild("uploadForm") uploadForm!: NgForm;

    async ngOnInit() {
        try {
            this.loading = true;

            // check to see if they are quick opening an item
            this.route.queryParams.subscribe(prm => {
                if (prm.ptp) {
                    this.isFromPTP = true;
                }

                if (prm.poid) {
                    this.poId = prm.poid;
                }
            });

            // is this is being used in a PTP PO editor tab?
            if (this.isFromPTP) {
                // when hosted in the ptp forms iframe the app shell already hides the nav/footer and
                // applies the embedded white/padded body styling — see AppComponent.updateShellVisibility
                let p2pDocType: InternalSettingDto = null;

                try {
                    [p2pDocType, this.poMetaData] = await Promise.all([
                        lastValueFrom(this.api.getApiUploadP2pDocumentType()),
                        lastValueFrom(this.api.getApiUploadPurchaseOrderMetadataPoId(this.poId))
                    ]);
                } catch (e) {
                    const errors = e.error?.ModelState?.status;

                    if (errors && Array.isArray(errors)) {
                        errors.forEach(status => {
                            this.message.error(status);
                        });
                    }
                } finally {
                    if (p2pDocType) {
                        // set the chosen type to the P2P doctype
                        this.documentType = parseInt(p2pDocType.KeyValue.trim());

                        // flag that a choice is already made
                        this.hasChosenType = true;
                        await this.GetSchema();
                        this.updateCommonFields();
                    }
                }
            } else {
                await this.GetSchema();

                // load the page normally
                this.documentType = this.schema.Types[0].Id;

                this.updateCommonFields();
            }
        } finally {
            this.loading = false;
        }
    }

    /*
     * Get and filter the allowed document types and only show those
     * to which the user has upload permission
     */
    GetSchema = async () => {
        const schema = await lastValueFrom(this.api.getApiSystemSchema());
        schema.Types = schema.Types.filter(t => t.CanUpload);
        this.schema = schema;
    };

    Cancel = () => {
        this.documentType = this.schema.Types[0].Id;
        this.hasChosenType = false;
        this.hasChosenFile = false;
        this.submitted = false;
        this.showTypeError = false;
        this.updateCommonFields();
    };

    OnFileSelected = (event: Event) => {
        const file: File = (event.target as HTMLInputElement).files[0];
        this.hasChosenFile = !!file;
    };

    KeywordIsRequired = (id: number) => {
        const type = this.schema.Types.find(t => t.Id == this.documentType);

        if (!type) return false;

        const keyword = type.Keywords.find(k => k.Id == id);

        if (!keyword) return false;

        return keyword.Required;
    };

    AnyKeywordIsRequired = () => {
        const type = this.schema.Types.find(t => t.Id == this.documentType);

        if (!type) return false;

        return type.Keywords.some(k => k.Required);
    };

    // Submits the add attachment form.
    Submit = async (frm: NgForm): Promise<void> => {
        this.submitted = true;
        const fileInput = document.getElementById("uploadFile") as HTMLInputElement | null;
        const file = fileInput?.files?.[0];
        this.hasChosenFile = !!file;
        if (frm.invalid) {
            // mark all fields as touched to show errors
            Object.keys(frm.controls).forEach(key => {
                frm.controls[key].markAsTouched();
            });
            return;
        }

        if (!file) {
            this.message.error("Please choose a file");
            return;
        }

        // collect keyword values from the rendered common fields
        const keywords: UploadKeywordDto[] = [];

        this.commonFields.forEach(item => {
            // never null: form-data appends a null as the literal string "null"; "" is treated as
            // "not supplied" by the server (skipped unless the keyword is required).
            let value = "";

            if (item.KeywordType === KeywordTypes.Date) {
                value = item.DateValue ? item.DateValue.toISOString() : "";
            } else {
                value = item.TextValue ?? "";
            }

            keywords.push({
                MetaDataDefinitionID: item.Id,
                KeywordType: item.KeywordType,
                Value: value
            });
        });

        this.loading = true;

        try {
            const result = await lastValueFrom(
                this.api.postApiUploadDocument({
                    file,
                    DocumentType: this.documentType,
                    PoId: this.poId,
                    Keywords: keywords
                })
            );

            if (result.Success) {
                this.SubmitSuccess();
            } else {
                if (result.Error) {
                    this.message.error(result.Error);
                }

                this.loading = false;
            }
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(status => this.message.error(status));
            } else {
                this.message.error(
                    typeof e.error === "string" ? e.error : e.error?.detail || "Failed to upload document."
                );
            }

            this.loading = false;
        }
    };

    SubmitSuccess = () => {
        // if this isn't in the PO editor, reset the chosen document type
        if (!this.isFromPTP) {
            this.documentType = null;
            this.hasChosenType = false;
        } else {
            // need to clear this file picker manually if in the PO editor
            const fileInput = document.getElementById("uploadFile") as HTMLInputElement | null;
            if (fileInput) fileInput.value = null;
        }

        this.hasChosenFile = false;
        this.submitted = false;

        // clear all date fields
        this.commonFields.filter(f => f.KeywordType === KeywordTypes.Date).forEach(f => (f.DateValue = null));

        if (this.uploadForm) {
            this.uploadForm.resetForm();
        }
        this.commonFields
            .filter(f => f.KeywordType !== KeywordTypes.Date)
            .forEach(f => {
                f.TextValue = this.poId && this.poMetaData ? String(this.poMetaData[f.Id] ?? "") : "";
            });

        this.loading = false;

        this.message.success("Uploaded successfully!");
    };

    /**
     * Keep track of the selected document type
     */
    SelectDocumentType = () => {
        if (this.documentType !== null && this.documentType !== 0) {
            this.hasChosenType = true;
            this.hasChosenFile = false;
            this.submitted = false;
            this.showTypeError = false;
        } else {
            this.showTypeError = true;
        }
    };

    /**
     * Updates underlying common fields based on document type selection.
     */
    updateCommonFields = () => {
        this.commonFields = this.getCommonKeywords();
    };

    /**
     * Retrieves common keywords based on dcoument type selection.
     * @returns Array of common keywords.
     */
    getCommonKeywords(): SchemaKeywordDtoExt[] {
        const keywords = [...this.schema.Keywords];
        const docTypes = this.schema.Types.filter(x => x.Id == this.documentType);

        if (docTypes.length !== 1) return [];

        const docType = docTypes[0];
        const filteredKeywords: SchemaKeywordDtoExt[] = keywords.filter(k =>
            docTypes.every(t => t.Keywords.some(dt => dt.Id === k.Id))
        );

        // override dateformat and regex with the document's metadata definition
        filteredKeywords.forEach(k => {
            const docTypeKeyword = docType.Keywords.find(dk => dk.Id == k.Id);
            k.DateFormat = docTypeKeyword.DateFormat;

            // not currently using the regex, but since we're in here
            k.Regex = docTypeKeyword.Regex;
            k.TextValue = this.poId && this.poMetaData ? String(this.poMetaData[k.Id] ?? "") : "";
        });

        // sort the resulting keywords by Required (true) and then alphabetical
        return filteredKeywords.sort((a, b) => {
            const first = docType.Keywords.find(k => k.Id === a.Id)?.Required || false;
            const second = docType.Keywords.find(k => k.Id === b.Id)?.Required || false;

            // sort by Required desc
            const requiredSort = Number(second) - Number(first);

            // then sort by name
            return requiredSort || a.Name.localeCompare(b.Name);
        });
    }
}
